import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { usePluginState } from "../../../usePluginState"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { useMemo, type JSX } from "react"
import { ButtonPrimary } from "@nova/component-btn"
import { createOrthoFlight } from "features/pubsub/entityActions/flights/ortho"
import { selectUserID } from "user/profile/selectors"
import { SampleDataFlightKey } from "@nova/constants"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { hideGlobalModal } from "runlevel/actions"

export const GenericTryOrthomosaicsModal = ({ supportsBack }: { supportsBack: boolean }) => {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)
    const { pluginsThatSupportSampleData } = usePluginState()
    const plugin = useMemo(
        () =>
            pluginsThatSupportSampleData.find((plugin) => plugin.featureId === NovaPluginIds.Ortho),
        [pluginsThatSupportSampleData]
    )

    const createNewOrthoFlight = (options: { isSample: boolean } = { isSample: false }) => {
        if (!userId) return

        dispatch(
            createOrthoFlight({
                data: {
                    createdBy: {
                        userId,
                    },
                },
                context: options.isSample
                    ? {
                          sampleDataFlightKey: SampleDataFlightKey.ortho2,
                      }
                    : undefined,
            })
        )

        dispatch(hideGlobalModal())
    }

    if (!plugin)
        throw `Ortho plugin is missing the "sampleDataModalName" property wherever it was registered.`

    return (
        <HighPriorityNovaModalContainer
            title={supportsBack ? "Back" : "Welcome to Nova"}
            icon={supportsBack ? undefined : Icons.NovaLogo}
            closeModal={() => dispatch(hideGlobalModal())}
            className="w-[44.75rem]"
            onReturn={
                supportsBack
                    ? () =>
                          dispatch(
                              initializeCenterModalForComponentAction(ModalNames.TryOurAddOnsModal)
                          )
                    : undefined
            }
        >
            <div className="mt-4 select-none">
                <h1 className="text-gray-0 mt-2.5 text-4xl">Make your first Orthomosaic</h1>

                <div className="flex gap-8 mt-6">
                    <TryOrthoOption
                        Icon={Icons.CardsStar}
                        title="Use my own Photos"
                        subtitle="Upload your own images to create an orthomosaic."
                        Button={
                            <ButtonPrimary
                                title="Upload Photos"
                                RightIcon={Icons.Upload}
                                className="w-fit"
                                onClick={() => createNewOrthoFlight()}
                            />
                        }
                    />

                    <TryOrthoOption
                        Icon={Icons.ArrowCoolDown}
                        title="Use Sample Data"
                        subtitle="Use our sample dataset to get familiar with the platform."
                        Button={
                            <ButtonPrimary
                                title="Use Sample Data"
                                RightIcon={Icons.ArrowRight}
                                className="w-fit"
                                onClick={() => createNewOrthoFlight({ isSample: true })}
                            />
                        }
                    />
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

const TryOrthoOption = ({
    Icon,
    title,
    subtitle,
    Button,
}: {
    Icon: Icons.IconComponent
    title: string
    subtitle: string
    Button: JSX.Element
}) => {
    return (
        <div className="flex flex-col gap-4 w-full">
            <Icons.ExtraLargeIcon Icon={Icon} className="text-lazeryellow" />
            <h2 className="text-gray-0">{title}</h2>
            <h4 className="text-gray-100 leading-[150%]">{subtitle}</h4>
            {Button}
        </div>
    )
}
